import { useCallback, useEffect, useState } from "react";
import { Download, ExternalLink, Loader2, RefreshCw, ZoomIn, ZoomOut, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
} from "@/components/ui/responsive-dialog";
import { Button } from "@/components/ui/button";
import { ReportTypeBadge } from "@/components/Badges";
import { downloadReportPdf, openReportPdf, openSamplePdf } from "@/lib/pdf";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const ZOOMS = [0.6, 0.8, 1, 1.25, 1.5];

/**
 * In-app PDF preview. The backend renders the PDF to page images (PyMuPDF), so the preview looks
 * identical on desktop, iOS and Android – no PDF plugin required.
 *
 * Exactly one source:
 *   report  -> saved report {id, report_number, report_type}
 *   payload -> unsaved ReportCreate payload (preview before saving)
 *   sample  -> company-profile sample report (settings page)
 */
export const PdfPreviewDialog = ({ open, onOpenChange, report, payload, sample = false, title }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [zoomIdx, setZoomIdx] = useState(2);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setData(null);
    try {
      let res;
      if (report?.id) res = await api.get(`/reports/${report.id}/preview`);
      else if (payload) res = await api.post("/reports/preview", payload);
      else if (sample) res = await api.get("/settings/company/sample-preview");
      else return;
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunne ikkje lage førehandsvising. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  }, [report?.id, payload, sample]);

  useEffect(() => {
    if (open) load();
    else { setData(null); setError(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, report?.id, sample]);

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "download") await downloadReportPdf(report);
      else if (kind === "open") await (report?.id ? openReportPdf(report) : openSamplePdf());
    } catch (e) {
      toast.error("Kunne ikkje hente PDF-fila.");
    } finally {
      setBusy("");
    }
  };

  const zoom = ZOOMS[zoomIdx];
  const number = data?.report_number || report?.report_number;
  const type = data?.report_type || report?.report_type || payload?.report_type;
  const isDraft = !!payload && !report?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[92vh] max-h-[92vh] w-[min(96vw,1000px)] max-w-[1000px] flex-col gap-0 rounded-[var(--radius)] p-0"
        drawerClassName="h-[96dvh] max-h-[96dvh]"
        data-testid="pdf-preview-dialog"
      >
        <DialogHeader className="shrink-0 border-b px-4 pb-3 pt-2 text-left md:px-5 md:py-3 md:pr-12">
          <div className="flex flex-wrap items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <DialogTitle className="font-heading text-base font-bold sm:text-lg" data-testid="pdf-preview-title">
              {title || (isDraft ? "Førehandsvising" : number || "PDF")}
            </DialogTitle>
            {type && <ReportTypeBadge type={type} />}
            {isDraft && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-foreground" data-testid="pdf-preview-draft-badge">Ikkje lagra</span>}
          </div>
          <DialogDescription className="text-xs" data-testid="pdf-preview-pagecount">
            {loading ? "Lagar PDF …" : data ? `${data.page_count} ${data.page_count === 1 ? "side" : "sider"} · slik kunden ser rapporten` : error ? "Feil" : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Pages */}
        <div className="pdf-preview-scroll flex-1 overflow-auto bg-[#E9EBEE] dark:bg-black/40" data-testid="pdf-preview-pages">
          {loading && (
            <div className="mx-auto flex w-full max-w-[720px] flex-col items-center gap-4 p-4 sm:p-6">
              <div className="relative aspect-[1/1.414] w-full rounded-sm bg-card shadow-md">
                <div className="absolute inset-x-0 top-0 h-[9%] rounded-t-sm bg-[#0B0D10]" />
                <div className="absolute inset-x-[7%] top-[14%] h-3 w-1/3 rounded bg-muted" />
                <div className="absolute inset-x-[7%] top-[20%] h-16 rounded bg-muted/70" />
                <div className="absolute inset-x-[7%] top-[36%] h-24 rounded bg-muted/60" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex items-center gap-2 rounded-full bg-card/95 px-4 py-2 text-sm shadow"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Lagar førehandsvising …</div>
                </div>
              </div>
            </div>
          )}
          {!loading && error && (
            <div className="mx-auto flex max-w-md flex-col items-center gap-3 p-10 text-center" data-testid="pdf-preview-error">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive"><AlertTriangle className="h-6 w-6" /></span>
              <p className="text-sm">{error}</p>
              <Button variant="outline" onClick={load} data-testid="pdf-preview-retry"><RefreshCw /> Prøv igjen</Button>
            </div>
          )}
          {!loading && data && (
            <div className="mx-auto flex flex-col items-center gap-4 p-3 sm:p-6" style={{ width: `min(100%, ${720 * zoom}px)` }}>
              {data.pages.map((p) => (
                <figure key={p.index} className="w-full" data-testid={`pdf-preview-page-${p.index}`}>
                  <img src={p.src} alt={`Side ${p.index}`} width={p.width} height={p.height} className="h-auto w-full rounded-sm bg-white shadow-md ring-1 ring-black/5" draggable={false} />
                  <figcaption className="mt-1.5 text-center text-[11px] text-muted-foreground">Side {p.index} av {data.page_count}</figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t bg-background px-3 pb-[calc(var(--safe-bottom)+10px)] pt-2.5 sm:px-5 md:pb-2.5" data-testid="pdf-preview-footer">
          <div className="hidden items-center gap-1 sm:flex">
            <Button variant="ghost" size="iconSm" onClick={() => setZoomIdx((z) => Math.max(0, z - 1))} disabled={zoomIdx === 0} aria-label="Zoom ut" data-testid="pdf-preview-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
            <span className="w-11 text-center text-xs tabular-nums text-muted-foreground">{Math.round(zoom * 100)}%</span>
            <Button variant="ghost" size="iconSm" onClick={() => setZoomIdx((z) => Math.min(ZOOMS.length - 1, z + 1))} disabled={zoomIdx === ZOOMS.length - 1} aria-label="Zoom inn" data-testid="pdf-preview-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
          </div>
          <div className={cn("ml-auto flex w-full gap-2 sm:w-auto", isDraft && "justify-end")}>
            {isDraft ? (
              <Button variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange?.(false)} data-testid="pdf-preview-close">Tilbake til skjemaet</Button>
            ) : (
              <>
                <Button variant="outline" className="flex-1 sm:flex-none" onClick={() => act("open")} disabled={!!busy || loading} data-testid="pdf-preview-open-tab">
                  {busy === "open" ? <Loader2 className="animate-spin" /> : <ExternalLink />} Opne i ny fane
                </Button>
                {report?.id && (
                  <Button className="flex-1 sm:flex-none" onClick={() => act("download")} disabled={!!busy || loading} data-testid="pdf-preview-download">
                    {busy === "download" ? <Loader2 className="animate-spin" /> : <Download />} Last ned PDF
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
